import React from 'react';
import { ScrollText, Sparkles, Wrench, Bug, CircleDot } from 'lucide-react';
import { RELEASES, CURRENT_RELEASE } from '../data/releaseNotes';

const formatDate = (iso) => {
  // Render as e.g. "16 May 2026" — locale-independent so it doesn't flip based on user system locale
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const Section = ({ icon: Icon, label, items, accent }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-4">
      <div className={`flex items-center gap-2 text-sm font-semibold ${accent}`}>
        <Icon className="w-4 h-4" />
        <span>{label}</span>
      </div>
      <ul className="mt-2 space-y-1.5">
        {items.map((line, i) => (
          <li key={i} className="flex gap-2 text-sm text-gray-700">
            <CircleDot className="w-3.5 h-3.5 mt-1 flex-shrink-0 text-gray-300" />
            <span>{line}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ReleaseCard = ({ release, isCurrent }) => (
  <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
    <div className="flex items-baseline justify-between flex-wrap gap-2">
      <div className="flex items-baseline gap-3 flex-wrap">
        <h2 className="text-2xl font-bold text-gray-900">v{release.version}</h2>
        <span className="text-gray-500">·</span>
        <span className="text-gray-600">{formatDate(release.date)}</span>
        {isCurrent && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
            Current release
          </span>
        )}
      </div>
    </div>
    {release.summary && (
      <p className="mt-2 text-gray-600">{release.summary}</p>
    )}

    <Section icon={Sparkles} label="New features"  items={release.new}          accent="text-emerald-700" />
    <Section icon={Wrench}   label="Improvements"  items={release.improvements} accent="text-blue-700"    />
    <Section icon={Bug}      label="Fixes"         items={release.fixes}        accent="text-rose-700"    />
  </div>
);

const ReleaseNotes = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center flex-shrink-0">
              <ScrollText className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">What's New</h1>
              <p className="text-gray-600 mt-1">
                {CURRENT_RELEASE ? (
                  <>
                    Currently running <span className="font-semibold">v{CURRENT_RELEASE.version}</span>,
                    released {formatDate(CURRENT_RELEASE.date)}.
                  </>
                ) : (
                  'Release notes begin with version 1.0, the first production release.'
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Releases. Written for production releases only, so the list is empty until v1.0. */}
        {RELEASES.length === 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-10 text-center text-gray-500">
            Nothing here yet. What changes in each release will be listed here from version 1.0.
          </div>
        )}
        {RELEASES.map((r, i) => (
          <ReleaseCard key={r.version} release={r} isCurrent={i === 0} />
        ))}
      </div>
    </div>
  );
};

export default ReleaseNotes;
